import {
    FC,
    MouseEvent,
    ReactElement,
    useState,
} from 'react'

import { Badge, Button, Comment, InfoListItem, ListGroupItem, MaterialIcon, UserColor } from '@/components'
import { IconCalendarComment } from '@/components/Icon/set'
import { convertDateToDateString, convertDateToDay, convertDateToTime } from '@/helpers/DateTimeParser'
import { getDeviatedTimeShift, shouldShowEndTime } from '@/helpers/tools'
import { trans } from '@/helpers/trans'
import { Shift, UserResource } from '@/models'
import { SettingResource } from '@/models/Setting'
import { ShiftStatus } from '@/models/Shift'

interface ShiftsListItemProps {
    shift: Shift
    onClick?: () => void
    currentUser?: UserResource | null
    settings?: SettingResource[]
    className?: string
}

export const ShiftsListItem: FC<ShiftsListItemProps> = ({
    shift,
    onClick = () => {},
    currentUser = null,
    settings = [],
    className = '',
}): ReactElement => {
    const [commentsVisible, setCommentsVisible] = useState(false)

    const shiftObject = new ShiftStatus(shift)
    const department = shift.department?.name ?? null
    const group = shift.department?.group?.name
    const userId = currentUser?.id
    const showEndTime = shouldShowEndTime(settings, shift.department?.id)
    const deviatedTime = getDeviatedTimeShift(shift, userId, showEndTime)
    const time = showEndTime ? `${convertDateToTime(shift.start)} - ${convertDateToTime(shift.end)}` : convertDateToTime(shift.start)
    const shiftPlanning = shift.shiftPlannings?.find(planning => planning.user?.id === userId)
    const color = shiftPlanning?.color || undefined
    const comments = shiftPlanning?.comments || []
    const icon = shiftObject.getIcon()

    const handleCommentButtonClick = (event: MouseEvent): void => {
        event.stopPropagation()
        setCommentsVisible(! commentsVisible)
    }

    return (
        <>
            <ListGroupItem
                onClick={onClick}
                className={className}
            >
                <div className="list-group-item__header text">
                    <div className="d-flex flex-row align-items-center">
                        { color && <UserColor color={color} className="mr-1" /> }
                        <div className="font-weight-bold">{convertDateToDay(shift.start)}</div>
                        <div className="text text-nowrap ml-1"> {`- ${convertDateToDateString(shift.start)}`}</div>
                    </div>
                    <div>
                        { time && deviatedTime ? deviatedTime : time }
                    </div>

                    {comments.length > 0 && (
                        <Button
                            text={trans('common.note')}
                            type="button"
                            onClick={handleCommentButtonClick}
                            className="list-group-item__button"
                        >
                            <IconCalendarComment />
                        </Button>
                    )}
                </div>
                <div className={`${shift.end ? 'text' : 'text-danger'} align-self-center d-flex flex-column`}>
                    <Badge className="ml-auto" color={shiftObject.getColor()}>
                        {shiftObject.getLabel()}
                        {icon && (
                            <span className="ml-1">
                                <MaterialIcon fontSize={18} kind={icon} color="white" />
                            </span>
                        )}
                    </Badge>
                    <div className="small mt-1 text-right">
                        {`${group ? `${group} - ` : ''}${department}`}
                    </div>
                </div>
            </ListGroupItem>

            {commentsVisible && (
                <div
                    onClick={handleCommentButtonClick}
                    className="list-group-item__comments"
                >
                    <InfoListItem label={trans('common.note')}>
                        <table>
                            <tbody>
                                <Comment comment={comments[comments.length - 1]} />
                            </tbody>
                        </table>
                    </InfoListItem>
                </div>
            )}
        </>
    )
}
